import * as React from 'react';

import classNames from 'classnames';

import {PageWrapperContext} from '../../../../../ui/layout/page-wrapper-context';
import {RouteContext} from '../../../../../ui/route-context';

import {ButtonGroup} from '../button-group/button-group';
import {EditableLabel} from '../editable-label/editable-label';

import {PageTitleBarTitle} from './page-title-bar-title';
import type {Breadcrumb} from './page-title-breadcrumbs';

import styles from './page-title-bar.module.css';

export type Props = {
    title: string;
    titleTooltip?: boolean;
    subheaderPrimaryText: string | React.ReactNode;
    buttons?: React.ReactNode;
    titleBadge?: React.ReactNode;
    isModified?: boolean;
    breadcrumbsTitle?: string;
    breadcrumbs?: Breadcrumb[];
    noMaxWidth?: boolean;
    subheaderSecondaryText?: string;
    // Pass these props if the title can be renamed in place
    editInPlaceProps?: {
        onChange: (newValue?: string | null | undefined) => Promise<any> | undefined;
        placeholder: string;
    };
    glossaryButton?: React.ReactNode;
};

export function PageTitleBar(props: Props) {
    const [titleValue, setTitleValue] = React.useState(props.title);

    React.useEffect(() => {
        setTitleValue(props.title);
    }, [props.title]);

    const {editInPlaceProps} = props;
    const getTitleComponent = () => {
        if (editInPlaceProps) {
            return (
                <EditableLabel
                    value={titleValue}
                    // @ts-expect-error
                    onSaveNewValue={(newValue) => {
                        setTitleValue(newValue);

                        return editInPlaceProps.onChange(newValue);
                    }}
                    placeholder={editInPlaceProps.placeholder}
                    shouldAllowUndo={false}
                    allowSavingEmpty={false}
                />
            );
        }
    };

    const {isEmbedded} = React.useContext(RouteContext);

    const styleName = classNames({
        [styles['page-title-bar']]: true,
        [styles['page-title-bar-with-navbar']]: !isEmbedded,
    });

    return (
        <PageWrapperContext.Consumer>
            {/* @ts-expect-error */}
            {({flashBanner}) => {
                return (
                    <>
                        <header className={styleName}>
                            <PageTitleBarTitle
                                title={titleValue}
                                titleTooltip={props.titleTooltip}
                                titleComponent={getTitleComponent()}
                                titleBadge={props.titleBadge}
                                breadcrumbs={props.breadcrumbs}
                                breadcrumbsTitle={props.breadcrumbsTitle}
                                subheaderPrimaryText={props.subheaderPrimaryText}
                                isModified={props.isModified}
                                subheaderSecondaryText={props.subheaderSecondaryText}
                                noMaxWidth={props.noMaxWidth}
                                isEditable={Boolean(props.editInPlaceProps)}
                                glossaryButton={props.glossaryButton}
                            />
                            {props.buttons && (
                                // id is used in tours, do not change without updating tour.ts
                                <div id='page-title-bar-buttons'>
                                    <ButtonGroup flexWrap={{justifyContent: 'end'}}>
                                        {props.buttons}
                                    </ButtonGroup>
                                </div>
                            )}
                        </header>
                        {flashBanner && (
                            <div className={styles['app-flash-banner']}>{flashBanner}</div>
                        )}
                    </>
                );
            }}
        </PageWrapperContext.Consumer>
    );
}
